<template>
  <b-col md="12" class="mb-3">
    <b-card class="formCardDash">
      <div class="card-header">
        <h6 class="card-title mb-0">Tally Reverse Invoice</h6>
        <small class="text-muted">Total Reverse Amount: {{ formatCurrency(totalReverseAmount) }} | Total Records: {{ totalRecords }}</small>
      </div>
      <div class="card-body">
        <!-- Filters -->
        <b-row class="mb-3">
          <b-col md="3">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search by document no, client name..."
              @input="debounceSearch"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-form-select
              v-model="perPage"
              :options="[10, 25, 50, 100]"
              @change="updatePagination"
              size="sm"
            />
          </b-col>
          <b-col md="2">
            <b-button
              variant="outline-info"
              size="sm"
              @click="refreshData"
              :disabled="isLoading"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
              Refresh
            </b-button>
          </b-col>
        </b-row>

        <!-- Bootstrap Table with Pagination -->
        <b-table
          :items="tallyReverseInvoiceData"
          :fields="fields"
          :busy="isLoading"
          striped
          bordered
          hover
          small
          show-empty
          empty-text="No reverse invoice data found"
          class="mb-0"
        >
          <template slot="table-busy">
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">Loading...</strong>
            </div>
          </template>

          <template slot="documentNo" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="documentDate" slot-scope="data">
            {{ formatDate(data.value) }}
          </template>

          <template slot="documentType" slot-scope="data">
            <b-badge variant="secondary">{{ data.value }}</b-badge>
          </template>

          <template slot="buyerLegalName" slot-scope="data">
            <strong>{{ data.value }}</strong>
            <br>
            <small class="text-muted">{{ data.item.buyerTradeName }}</small>
          </template>

          <template slot="jobDetails.JobNo" slot-scope="data">
            <code>{{ data.value }}</code>
          </template>

          <template slot="jobDetails.POL" slot-scope="data">
            <small>{{ data.value }} → {{ data.item.jobDetails.POD }}</small>
          </template>

          <template slot="amount" slot-scope="data">
            <strong class="text-right d-block">{{ formatCurrency(data.value) }}</strong>
          </template>

          <template slot="items" slot-scope="data">
            <b-badge variant="primary">{{ data.value.length || 0 }}</b-badge>
          </template>

          <template slot="actions" slot-scope="data">
            <b-button
              size="sm"
              variant="outline-info"
              @click.stop="toggleDetails(data.item)"
              class="toggle-btn"
            >
              <i :class="data.item._showDetails ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </b-button>
          </template>

          <!-- Row Details Slot -->
          <template slot="row-details" slot-scope="row">
            <div class="row-details">
              <!-- Job Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-primary">
                    <div class="card-header bg-primary text-white">
                      <i class="fa fa-info-circle"></i> Job Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Job Date:</strong> {{ formatDate(row.item.jobDetails.JobDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised By:</strong> {{ row.item.jobDetails.RaisedBy }}
                        </div>
                        <div class="col-md-3">
                          <strong>Shipping Line:</strong> {{ row.item.jobDetails.SlineName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Vessel:</strong> {{ row.item.jobDetails.VesselName }} ({{ row.item.jobDetails.VoyageNo }})
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>SOB:</strong> {{ formatDate(row.item.jobDetails.SOB) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Master BL:</strong> {{ row.item.jobDetails.MasterBL || 'N/A' }}
                        </div>
                        <div class="col-md-3">
                          <strong>House BL:</strong> {{ row.item.jobDetails.HouseBL || 'N/A' }}
                        </div>
                        <div class="col-md-3">
                          <strong>Raised From:</strong> {{ row.item.jobDetails.RaisedFrom }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Location Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-success">
                    <div class="card-header bg-success text-white">
                      <i class="fa fa-map-marker"></i> Location Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-2">
                          <strong>POR:</strong> {{ row.item.jobDetails.POR }}
                        </div>
                        <div class="col-md-2">
                          <strong>POL:</strong> {{ row.item.jobDetails.POL }}
                        </div>
                        <div class="col-md-2">
                          <strong>POD:</strong> {{ row.item.jobDetails.POD }}
                        </div>
                        <div class="col-md-2">
                          <strong>FPOD:</strong> {{ row.item.jobDetails.FPOD }}
                        </div>
                        <div class="col-md-2">
                          <strong>Stuffing:</strong> {{ row.item.jobDetails.STUFF }}
                        </div>
                        <div class="col-md-2">
                          <strong>Handover:</strong> {{ row.item.jobDetails.HAND }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Client & Enquiry Details -->
              <div class="row mb-3">
                <div class="col-12">
                  <div class="card border-warning">
                    <div class="card-header bg-warning">
                      <i class="fa fa-users"></i> Client & Enquiry Details
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div class="col-md-3">
                          <strong>Main Client:</strong> {{ row.item.jobDetails.ClientName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Exporter:</strong> {{ row.item.jobDetails.ExpClientName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Importer:</strong> {{ row.item.jobDetails.ImpClientName }}
                        </div>
                        <div class="col-md-3">
                          <strong>Enquiry:</strong> {{ row.item.jobDetails.EnqNo }}
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-md-3">
                          <strong>Enquiry Date:</strong> {{ formatDate(row.item.jobDetails.EnqDate) }}
                        </div>
                        <div class="col-md-3">
                          <strong>Billing Type:</strong> {{ row.item.jobDetails.BillingType }}
                        </div>
                        <div class="col-md-3">
                          <strong>BL Type:</strong> {{ row.item.jobDetails.BLType }}
                        </div>
                        <div class="col-md-6">
                          <strong>Login User:</strong> {{ row.item.jobDetails.LoginUserName }} ({{ row.item.jobDetails.LoginUserEmail }})
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Invoice Items -->
              <div class="row mb-3" v-if="row.item.items && row.item.items.length > 0">
                <div class="col-12">
                  <div class="card border-danger">
                    <div class="card-header bg-danger text-white">
                      <i class="fa fa-list"></i> Invoice Items ({{ row.item.items.length }})
                    </div>
                    <div class="card-body p-0">
                      <b-table
                        :items="row.item.items"
                        :fields="itemFields"
                        small
                        bordered
                        class="mb-0"
                      >
                        <template slot="unitPrice" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                        <template slot="totalAmount" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modules -->
              <div class="row mb-3" v-if="row.item.modules && row.item.modules.length > 0">
                <div class="col-12">
                  <div class="card border-info">
                    <div class="card-header bg-info text-white">
                      <i class="fa fa-cubes"></i> Modules ({{ row.item.modules.length }})
                    </div>
                    <div class="card-body p-0">
                      <b-table
                        :items="row.item.modules"
                        :fields="moduleFields"
                        small
                        bordered
                        class="mb-0"
                      >
                        <template slot="unitPrice" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                        <template slot="totalAmount" slot-scope="data">
                          {{ formatCurrency(data.value) }}
                        </template>
                      </b-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-pagination
          v-if="totalItems > pagination.perPage"
          limit="25"
          align="center"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total-rows="totalItems"
          @change="onPageChange"
          class="mt-3"
        ></b-pagination>

        <!-- Table Info -->
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>
            <b-form-select
              v-model="pagination.perPage"
              :options="pagination.pageOptions"
              @change="updatePagination"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">per page</span>
          </div>

          <div class="text-muted small">
            Total Records: {{ totalItems }} | Total Amount: {{ formatCurrency(totalReverseAmount) }}
          </div>
        </div>

      </div>
    </b-card>
  </b-col>
</template>

<script>
import moment from "moment";
import SalesPersonTeusService from "../../../services/SalesPersonTeusService";

export default {
  name: "TallyReverseInvoiceSection",
  props: {
    tallyReverseInvoiceData: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      searchQuery: '',
      searchTimeout: null,
      isLoading: false,
      totalItems: 0,
      totalPages: 1,
      filter: null,
      pagination: {
        currentPage: 1,
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
      },
      fields: [
        { key: 'documentNo', label: 'Document No', sortable: true },
        { key: 'documentDate', label: 'Date', sortable: true },
        { key: 'documentType', label: 'Type', sortable: false },
        { key: 'ledgerGroup', label: 'Ledger Group', sortable: true },
        { key: 'buyerLegalName', label: 'Client Name', sortable: true },
        { key: 'jobDetails.JobNo', label: 'Job No', sortable: true },
        { key: 'jobDetails.JobType', label: 'Job Type', sortable: true },
        { key: 'jobDetails.JobTransportType', label: 'Transport', sortable: true },
        { key: 'jobDetails.POL', label: 'POL → POD', sortable: false },
        { key: 'amount', label: 'Amount', sortable: true, class: 'text-right' },
        { key: 'items', label: 'Items', sortable: false, class: 'text-center' },
        { key: 'actions', label: 'Actions', sortable: false, class: 'text-center' }
      ],
      itemFields: [
        { key: 'slNo', label: 'SL No', class: 'text-center' },
        { key: 'description', label: 'Description' },
        { key: 'quantity', label: 'Qty', class: 'text-right' },
        { key: 'unitPrice', label: 'Unit Price', class: 'text-right' },
        { key: 'gstRate', label: 'GST Rate', class: 'text-right' },
        { key: 'totalAmount', label: 'Amount', class: 'text-right' }
      ],
      moduleFields: [
        { key: 'description', label: 'Description' },
        { key: 'quantity', label: 'Qty', class: 'text-right' },
        { key: 'unitPrice', label: 'Unit Price', class: 'text-right' },
        { key: 'totalAmount', label: 'Amount', class: 'text-right' }
      ]
    };
  },
  computed: {
    totalReverseAmount() {
      return this.tallyReverseInvoiceData.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return "₹0";
      return "₹" + Number(value).toLocaleString("en-IN");
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD-MM-YYYY");
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filter = this.searchQuery;
      }, 500);
    },

    updatePagination() {
      this.pagination.currentPage = 1;
    },

    onFiltered(filteredItems) {
      // Trigger pagination reset when filtered
      this.pagination.currentPage = 1;
    },

    onPageChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },

    toggleDetails(invoice) {
      // Toggle the _showDetails property for BootstrapVue row-details
      invoice._showDetails = !invoice._showDetails;
      this.$forceUpdate(); // Force update to trigger reactivity
    },

    async loadData() {
      this.isLoading = true;
      try {
        // For b-table with client-side pagination, we can load all data at once
        // or implement server-side pagination if needed
        const payload = {
          salesPersonId: this.$store.state.user.userId,
          clientId: this.$store.state.client.clientId || null,
          dateRange: this.$store.state.dateRange || '2025-2026',
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
          search: this.searchQuery
        };

        const response = await SalesPersonTeusService.getTallyReverseInvoiceData(payload);

        if (response.status) {
          // Initialize _showDetails property for each item
          const data = response.data.reverseInvoices.map(item => {
            const newItem = Object.assign({}, item);
            newItem._showDetails = false;
            return newItem;
          });

          this.$emit('data-loaded', {
            data: data,
            pagination: response.data.pagination,
            summary: response.data.summary
          });
          this.totalItems = response.data.pagination.totalItems;
          this.totalPages = response.data.pagination.totalPages;
        }
      } catch (error) {
        console.error('Error loading tally reverse invoice data:', error);
        this.$emit('data-loaded', {
          data: [],
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: this.pagination.perPage },
          summary: { totalReverseAmount: 0, totalRecords: 0 }
        });
        this.totalItems = 0;
      } finally {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.loadData();
    },
  },

  mounted() {
    this.loadData();
  },

  watch: {
    tallyReverseInvoiceData: {
      handler() {
        // Reset pagination when data changes
        this.pagination.currentPage = 1;
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.formCardDash {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.expanded-details {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 15px;
}

.expanded-details .row {
  margin-bottom: 10px;
}

.expanded-details h6 {
  margin-bottom: 10px;
  font-weight: 600;
}

.expanded-details table.table-sm {
  font-size: 12px;
}

.expanded-details table.table-sm td {
  padding: 4px 8px;
  border: none;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table th {
  position: sticky;
  top: 0;
  background-color: #007bff !important;
  color: white !important;
  z-index: 10;
  border-color: #0056b3;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

.table thead th {
  border-bottom: 2px solid #0056b3;
}

.table tbody td {
  vertical-align: middle;
  padding: 8px;
}

.table tbody td.text-center {
  text-align: center;
}

.table tbody td.text-right {
  text-align: right;
}

.badge {
  font-size: 11px;
}

.btn-outline-info {
  border-color: #17a2b8;
  color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.toggle-btn {
  min-width: 40px;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .expanded-details .col-md-6 {
    margin-bottom: 20px;
  }

  .table-responsive {
    max-height: 400px;
  }
}
</style>



// WEBPACK FOOTER //
// TallyReverseInvoiceSection.vue?055d2e62